import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useSnackbar } from "notistack";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { OrganizationDetailsDto } from "~/api/GeneratedApi";
import { loginRequest, renewSilentRequest, silentRequest } from "~/config";
import { selectUser, setUser } from "~/redux/reducers/authReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";

export interface User {
  id?: number;
  username: string;
  email: string;
  organization: OrganizationDetailsDto;
  isAdmin: boolean;
  isOrganizationAdmin: boolean;
  alarmsMuted: boolean;
  arrivalsMuted: boolean;
}

interface AuthMethods {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | undefined>;
  refresh: () => Promise<void>;
  isLoggedIn: () => boolean;
  loginProgress: string;
  user: User | null;
}

function useAuth(): AuthMethods {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("login");
  const user = useAppSelector(selectUser);
  const { enqueueSnackbar } = useSnackbar();
  const isAuthenticated = useIsAuthenticated();

  const { instance, inProgress } = useMsal();

  const login = useCallback(
    () => instance.loginRedirect(loginRequest),
    [instance],
  );

  const logout = useCallback(
    () =>
      new Promise<void>((resolve) => {
        instance
          .logoutRedirect({
            account: instance.getActiveAccount(),
            postLogoutRedirectUri: `${window.location.origin}/loggedout`,
            authority: "https://login.microsoftonline.com/common/",
          })
          .then(() => {
            dispatch(setUser(null));
            localStorage.removeItem("token");
            resolve();
          })
          .catch(() => {
            enqueueSnackbar(t("LogoutFailed"), { variant: "error" });
          });
      }),
    [instance, dispatch, enqueueSnackbar, t],
  );

  const getToken = useCallback(
    () =>
      new Promise<string | undefined>((resolve, reject) => {
        instance
          .acquireTokenSilent(silentRequest)
          .then((x) => resolve(x.accessToken))
          .catch(async (error) => {
            console.log("err in acquireTokenSilent %O", error);
            if (error instanceof InteractionRequiredAuthError) {
              // fallback to interaction when silent call fails
              return instance
                .acquireTokenRedirect(renewSilentRequest)
                .catch(reject);
            }
            return reject(error);
          });
      }),
    [instance],
  );

  const refresh = () =>
    new Promise<void>((resolve) => {
      resolve();
    });

  const isLoggedIn = () => isAuthenticated;

  return {
    login,
    logout,
    getToken,
    refresh,
    isLoggedIn,
    user,
    loginProgress: inProgress,
  };
}

export default useAuth;
